import { useEffect, useState } from 'react';
import { 
    HomeModernIcon, 
    DocumentTextIcon, 
    CurrencyDollarIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';
import { getProprietaireStats, DashboardStats } from '../../service/dashboard/dashboard.service';
import { getBiensRecents } from '../../service/biens/biens.service';

export default function DashboardProprietaire() {
    const [stats, setStats] = useState<DashboardStats>({
        totalBiens: 0,
        contratsActifs: 0,
        revenusTotal: 0,
        locataires: 0
    });
    const [biensRecents, setBiensRecents] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getProprietaireStats();
                setStats(data);
            } catch (err) {
                console.error('Erreur lors du chargement des statistiques:', err);
                setError('Impossible de charger les statistiques');
            } finally {
                setLoading(false);
            }
        };
        const fetchBiensRecents = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getBiensRecents();
                setBiensRecents(data);
            } catch (err) {
                console.error('Erreur lors du chargement des biens récents:', err);
                setError('Impossible de charger les biens récents');
            } finally {
                setLoading(false);
            }
        };
        
        fetchStats();
        fetchBiensRecents();
    }, []);

    const statCards = [
        { 
            title: 'Mes biens', 
            value: stats.totalBiens, 
            icon: HomeModernIcon, 
            color: 'bg-blue-500',
            textColor: 'text-blue-600',
            bgLight: 'bg-blue-50'
        },
        { 
            title: 'Contrats actifs', 
            value: stats.contratsActifs, 
            icon: DocumentTextIcon, 
            color: 'bg-green-500',
            textColor: 'text-green-600',
            bgLight: 'bg-green-50'
        },
        { 
            title: 'Revenus mensuels', 
            value: `${stats.revenusTotal}€`, 
            icon: CurrencyDollarIcon, 
            color: 'bg-purple-500',
            textColor: 'text-purple-600',
            bgLight: 'bg-purple-50'
        },
        { 
            title: 'Locataires', 
            value: stats.locataires, 
            icon: UserGroupIcon, 
            color: 'bg-orange-500',
            textColor: 'text-orange-600',
            bgLight: 'bg-orange-50'
        }
    ];

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des statistiques...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* En-tête */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Dashboard Propriétaire</h1>
                <p className="mt-2 text-gray-600">Gérez vos biens immobiliers</p>
            </div>

            {/* Cartes de statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat, index) => (
                    <div key={index} className={`${stat.bgLight} rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                                <p className={`text-3xl font-bold mt-2 ${stat.textColor}`}>{stat.value}</p>
                            </div>
                            <div className={`${stat.color} p-3 rounded-lg`}>
                                <stat.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Actions rapides */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Actions rapides</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button className="flex items-center justify-center p-4 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                        <HomeModernIcon className="h-5 w-5 mr-2" />
                        Ajouter un bien
                    </button>
                    <button className="flex items-center justify-center p-4 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                        <DocumentTextIcon className="h-5 w-5 mr-2" />
                        Nouveau contrat
                    </button>
                    <button className="flex items-center justify-center p-4 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                        <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                        Voir les paiements
                    </button>
                </div>
            </div>

            {/* Mes biens */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Mes biens récents</h2>
                <div className="space-y-3">
                    {biensRecents.length === 0 ? (
                        <p className="text-gray-600">Aucun bien récent à afficher.</p>
                    ) : (
                        biensRecents.map((bien) => (
                            <div key={bien.bienId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">  
                                    <div className="flex items-center">
                                        <HomeModernIcon className="h-8 w-8 text-blue-600 mr-4" />
                                        <div>
                                            <p className="font-medium text-gray-900">{bien.adresse}{bien.adresse}</p>
                                            <p className="text-sm text-gray-500">{bien.superficie} m² • {bien.nombreDePieces} pièces</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-gray-900">1,200€/mois</p>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Loué
                                        </span>
                                    </div>
                                </div>
                        ))
                    )}
                </div>
            </div>

            {/* Paiements à venir */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Paiements attendus</h2>
                <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                            <p className="font-medium text-gray-900">Jean Dupont</p>
                            <p className="text-sm text-gray-500">Appartement T3 - Paris 15e</p>
                        </div>
                        <div className="text-right">
                            <p className="font-semibold text-green-600">1,200€</p>
                            <p className="text-xs text-gray-500">Attendu le 5 déc.</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                            <p className="font-medium text-gray-900">Marie Martin</p>
                            <p className="text-sm text-gray-500">Studio - Lyon 3e</p>
                        </div>
                        <div className="text-right">
                            <p className="font-semibold text-green-600">650€</p>
                            <p className="text-xs text-gray-500">Attendu le 5 déc.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
